import { Controller } from "@hotwired/stimulus";
import { create } from "filepond";
import Axios from "axios";
import "abortcontroller-polyfill/dist/abortcontroller-polyfill-only";
import { flashDanger, uploadSizeError } from "../lib/flash";

// Uploads a loose reference file onto a note's Files (the Attachments panel). Modeled on
// the old Documents uploader, but posts to the note-scoped notes/attachments#create endpoint
// (data-upload-url) with no client-supplied filepath — the server pins every upload to this
// note's own "/attachments/note/:id/files" folder (see Note#attachments_filepath). FilePond is
// mounted on the panel's file input, with its credits link hidden. There is no modal to reload on close here: a successful upload applies the
// pre-rendered Turbo Stream the server returns (prepends the row, updates the count) instead of
// re-visiting the note.
export default class extends Controller {
  connect() {
    const pond = (this.pond = create(this.element.querySelector('input[type="file"]'), {
      storeAsFile: true,
      credits: false,
      // Its drop label is hidden (see _notes.scss): files come in by the "Attach files" buttons or a
      // drop anywhere on the note, and the pond only shows their progress.
      labelIdle: "",
      // The item shows why it failed (too large, or upload failed) rather than FilePond's own label.
      labelFileProcessingError: (error) => error?.body || this.element.dataset.uploadFailedMessage,
      server: {
        process: async (fieldName, file, metadata, load, error, progress, abort) => {
          const tooLarge = uploadSizeError(file, this.element);
          if (tooLarge) {
            error(tooLarge);
            return;
          }

          let attachmentId;
          const controller = new AbortController();

          try {
            const created = await Axios.post(this.element.dataset.uploadUrl, {
              data: { filename: file.name, size: file.size, content_type: file.type },
            });
            attachmentId = created.data.id;

            const formData = new FormData();
            Object.entries(created.data.fields).forEach(([key, value]) => {
              formData.append(key, value);
            });
            formData.append("file", file);

            await Axios({
              url: created.data.url,
              method: "POST",
              data: formData,
              signal: controller.signal,
              onUploadProgress: (e) => {
                progress(e.lengthComputable, e.loaded, e.total);
              },
            });

            Turbo.renderStreamMessage(created.data.stream);
            load(attachmentId);
          } catch (e) {
            if (attachmentId) this.abandon(attachmentId);
            error(this.element.dataset.uploadFailedMessage);
          }

          return {
            abort: () => {
              controller.abort();
              if (attachmentId) this.abandon(attachmentId);
              abort();
            },
          };
        },
      },
    }));

    pond.on("error", (error) => {
      if (!error) return;

      flashDanger(this.element.dataset.uploadFailedMessage);
    });

    // The real row is already in the list (applied via the Turbo Stream above) by the time this
    // fires, so FilePond's own item — otherwise left showing a "done" state indefinitely — can be
    // dropped straight away.
    pond.on("processfile", (error, file) => {
      if (!error) pond.removeFile(file.id);
    });
  }

  // Files dropped elsewhere on the note's page (note_drop_controller.js), queued as if picked here.
  addFiles(files) {
    this.pond.addFiles(files);
  }

  // The "Attach files" buttons open the file picker.
  browse() {
    this.pond.browse();
  }

  // Cleans up the Attachment record a cancelled or failed direct-to-S3 upload left behind — it's
  // saved before that S3 POST (see Attachments::Uploadable#create), so a network error or a
  // user-initiated cancel would otherwise leave a permanent stub (RemoveOrphans deliberately
  // excludes this filepath shape — see Note#attachments_filepath). The server re-checks ownership
  // and that the S3 object truly doesn't exist before deleting anything
  // (Notes::Attachments::AbandonmentsController#destroy).
  abandon(attachmentId) {
    Axios.delete(`${this.element.dataset.uploadUrl}/${attachmentId}/abandonment`).catch(() => {});
  }
}
